// pages/Admin/ConferenceAdmin.jsx
// Route: /Conference
// Manages: Conference Overview (left text, stats, focus areas) + Feedback section

import React, { useState, useEffect } from "react";
import axios from "../../api/axiosInstance";
import App from "../../Utils/LayoutMobile";

/* ─── Tokens ─────────────────────────────────────────────── */
const C_PINK   = "#F04DA0";
const C_PURPLE = "#8B2FC9";
const C_DARK   = "#1a0040";
const C_GRAD   = `linear-gradient(135deg,${C_PINK},${C_PURPLE})`;

/* ─── Toast ──────────────────────────────────────────────── */
let _tid = 0;
function useToast() {
  const [toasts, setToasts] = useState([]);
  const add = (msg, type = "success") => {
    const id = ++_tid;
    setToasts((p) => [...p, { id, msg, type }]);
    setTimeout(() => setToasts((p) => p.filter((t) => t.id !== id)), 3500);
  };
  return { toasts, success: (m) => add(m, "success"), error: (m) => add(m, "error") };
}

/* ─── Icon Map ────────────────────────────────────────────── */
const ICON_OPTIONS = [
  { key: "globe",  svg: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/><path d="M2 12h20"/></svg> },
  { key: "grid",   svg: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg> },
  { key: "pulse",  svg: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg> },
  { key: "star",   svg: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg> },
  { key: "users",  svg: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg> },
  { key: "layers", svg: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg> },
];

/* ─── Reusable field components ────────────────────────────── */
function Field({ label, children }) {
  return (
    <div className="ca-field">
      {label && <label className="ca-label">{label}</label>}
      {children}
    </div>
  );
}

function SectionCard({ title, subtitle, icon, iconColor, iconBg, delay = 0, children }) {
  return (
    <div className="ca-card" style={{ animationDelay: `${delay}s` }}>
      <div className="ca-card-hdr">
        <span className="ca-card-ico" style={{ color: iconColor, background: iconBg }}>{icon}</span>
        <div>
          <h2 className="ca-card-title">{title}</h2>
          {subtitle && <p className="ca-card-sub">{subtitle}</p>}
        </div>
      </div>
      <div className="ca-card-body">{children}</div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════ */
export default function ConferenceAdmin() {
  const toast   = useToast();
  const [tab, setTab]       = useState("overview");
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);

  /* ── Conference Overview state ── */
  const [conf, setConf] = useState({
    eyebrowText:          "About the Event",
    titleLeftMain:        "Conference",
    titleLeftHighlight:   "Overview",
    description:          "",
    summaryPoints:        [],
    stats:                [{ value: "", label: "" }],
    titleRightMain:       "Area of",
    titleRightHighlight:  "Focus",
    focusAreas:           [{ title: "", desc: "", iconKey: "globe" }],
  });

  /* ── Feedback state ── */
  const [fb, setFb] = useState({
    eyebrowText:    "Participant Insights",
    titleMain:      "Did you",
    titleHighlight: "know?",
    para1:          "",
    para2:          "",
    stats:          [
      { value: "98", label: "HIGHLY SATISFIED" },
      { value: "96", label: "WOULD RECOMMEND" },
    ],
  });

  /* ── Fetch ── */
  useEffect(() => {
    axios.get("/api/v1/page-content?type=conference")
      .then((res) => {
        const d = res.data?.data || {};
        if (d.confOverview) {
          const co = d.confOverview;
          setConf({
            eyebrowText:         co.eyebrowText         || "About the Event",
            titleLeftMain:       co.titleLeftMain        || "Conference",
            titleLeftHighlight:  co.titleLeftHighlight   || "Overview",
            description:         co.description          || "",
            summaryPoints:       co.summaryPoints?.length ? co.summaryPoints : [],
            stats:               co.stats?.length        ? co.stats          : [{ value: "", label: "" }],
            titleRightMain:      co.titleRightMain        || "Area of",
            titleRightHighlight: co.titleRightHighlight   || "Focus",
            focusAreas:          co.focusAreas?.length    ? co.focusAreas    : [{ title: "", desc: "", iconKey: "globe" }],
          });
        }
        if (d.feedback) {
          const f = d.feedback;
          setFb({
            eyebrowText:    f.eyebrowText    || "Participant Insights",
            titleMain:      f.titleMain      || "Did you",
            titleHighlight: f.titleHighlight || "know?",
            para1:          f.para1          || "",
            para2:          f.para2          || "",
            stats:          f.stats?.length  ? f.stats : [{ value: "98", label: "HIGHLY SATISFIED" }, { value: "96", label: "WOULD RECOMMEND" }],
          });
        }
      })
      .catch(() => toast.error("Failed to load data"))
      .finally(() => setLoading(false));
  }, []);

  /* ── Save both tabs together ── */
  const save = async () => {
    setSaving(true);
    try {
      await axios.post("/api/v1/page-content/conference", {
        confOverview: conf,
        feedback:     fb,
      });
      toast.success("Conference content saved!");
    } catch (err) {
      toast.error(err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  /* ── conf helpers ── */
  const cSet = (k, v)       => setConf((p) => ({ ...p, [k]: v }));
  const addStat    = ()     => setConf((p) => ({ ...p, stats: [...p.stats, { value: "", label: "" }] }));
  const removeStat = (i)    => setConf((p) => ({ ...p, stats: p.stats.filter((_, idx) => idx !== i) }));
  const updStat  = (i,k,v)  => setConf((p) => { const s=[...p.stats]; s[i]={...s[i],[k]:v}; return {...p,stats:s}; });
  const addFocus   = ()     => setConf((p) => ({ ...p, focusAreas: [...p.focusAreas, { title:"", desc:"", iconKey:"globe" }] }));
  const removeFocus= (i)    => setConf((p) => ({ ...p, focusAreas: p.focusAreas.filter((_,idx)=>idx!==i) }));
  const updFocus = (i,k,v)  => setConf((p) => { const f=[...p.focusAreas]; f[i]={...f[i],[k]:v}; return {...p,focusAreas:f}; });
  const addSum   = ()       => setConf((p) => ({ ...p, summaryPoints: [...p.summaryPoints, { bold:"", text:"" }] }));
  const removeSum= (i)      => setConf((p) => ({ ...p, summaryPoints: p.summaryPoints.filter((_,idx)=>idx!==i) }));
  const updSum   = (i,k,v)  => setConf((p) => { const s=[...p.summaryPoints]; s[i]={...s[i],[k]:v}; return {...p,summaryPoints:s}; });

  /* ── fb helpers ── */
  const fSet = (k, v)       => setFb((p) => ({ ...p, [k]: v }));
  const addFbStat    = ()   => setFb((p) => ({ ...p, stats: [...p.stats, { value:"", label:"" }] }));
  const removeFbStat = (i)  => setFb((p) => ({ ...p, stats: p.stats.filter((_,idx)=>idx!==i) }));
  const updFbStat = (i,k,v) => setFb((p) => { const s=[...p.stats]; s[i]={...s[i],[k]:v}; return {...p,stats:s}; });

  /* ── Shared icons ── */
  const PlusIcon  = <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
  const TrashIcon = <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>;
  const SaveIcon  = <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>;

  const OverviewIcon = <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>;
  const FbIcon       = <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>;
  const FocusIcon    = <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>;

  return (
    <App>
      <div className="ca-page">

        {/* Toasts */}
        <div className="ca-toast-wrap">
          {toast.toasts.map((t) => (
            <div key={t.id} className={`ca-toast ca-toast--${t.type}`}>
              {t.type === "success"
                ? <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
                : <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
              }
              <span>{t.msg}</span>
            </div>
          ))}
        </div>

        {/* Page Header */}
        <div className="ca-header">
          <div className="ca-eyebrow"><span className="ca-eyebrow-dot"/>Scientific Program</div>
          <div className="ca-header-row">
            <div>
              <h1 className="ca-page-title">Conference <span className="ca-grad">Content</span></h1>
              <p className="ca-page-sub">Manage Conference Overview, Focus Areas, and Feedback section.</p>
            </div>
            <button className="ca-save-btn" onClick={save} disabled={saving || loading}>
              {saving
                ? <><span className="ca-btn-spin"/><span>Saving…</span></>
                : <>{SaveIcon}<span>Save All Changes</span></>
              }
            </button>
          </div>
        </div>

        {loading ? (
          <div className="ca-loading"><div className="ca-spinner"/><span>Loading…</span></div>
        ) : (
          <>
            {/* Tabs */}
            <div className="ca-tabs">
              <button className={`ca-tab ${tab==="overview"?"ca-tab--active":""}`} onClick={()=>setTab("overview")}>
                {OverviewIcon} Conference Overview
              </button>
              <button className={`ca-tab ${tab==="feedback"?"ca-tab--active":""}`} onClick={()=>setTab("feedback")}>
                {FbIcon} Feedback Section
              </button>
            </div>

            {/* ══ OVERVIEW TAB ══ */}
            {tab === "overview" && (
              <div className="ca-panel">

                {/* Left side text */}
                <SectionCard
                  title="Left Side — Overview Text"
                  subtitle="Eyebrow badge, title, description paragraph and summary bullet points"
                  icon={OverviewIcon} iconColor={C_PINK} iconBg="rgba(240,77,160,0.1)" delay={0.05}
                >
                  <div className="ca-grid-3">
                    <Field label="Eyebrow Badge Text">
                      <input className="ca-input" value={conf.eyebrowText}
                        onChange={(e)=>cSet("eyebrowText",e.target.value)}
                        placeholder="About the Event"/>
                    </Field>
                    <Field label="Title — Main Word">
                      <input className="ca-input" value={conf.titleLeftMain}
                        onChange={(e)=>cSet("titleLeftMain",e.target.value)}
                        placeholder="Conference"/>
                    </Field>
                    <Field label="Title — Pink Highlight">
                      <input className="ca-input" value={conf.titleLeftHighlight}
                        onChange={(e)=>cSet("titleLeftHighlight",e.target.value)}
                        placeholder="Overview"/>
                    </Field>
                  </div>

                  <Field label="Description Paragraph">
                    <textarea className="ca-textarea" rows={4} value={conf.description}
                      onChange={(e)=>cSet("description",e.target.value)}
                      placeholder="Join us for the Skinola International Aesthetic Conference 2026…"/>
                  </Field>

                  {/* Summary Points */}
                  <div className="ca-sub-hdr">
                    <div>
                      <p className="ca-sub-title">Summary Bullet Points</p>
                      <p className="ca-sub-desc">Bold + regular text items below the description</p>
                    </div>
                    <button className="ca-add-btn" onClick={addSum}>{PlusIcon} Add Point</button>
                  </div>

                  <div className="ca-list">
                    {conf.summaryPoints.length === 0 && (
                      <p className="ca-empty-sm">No summary points added yet.</p>
                    )}
                    {conf.summaryPoints.map((sp, i) => (
                      <div key={i} className="ca-list-row">
                        <div className="ca-list-num">{i+1}</div>
                        <div className="ca-grid-2 ca-list-inputs">
                          <input className="ca-input" value={sp.bold}
                            onChange={(e)=>updSum(i,"bold",e.target.value)}
                            placeholder="Bold text" style={{fontWeight:700}}/>
                          <input className="ca-input" value={sp.text}
                            onChange={(e)=>updSum(i,"text",e.target.value)}
                            placeholder="Regular text"/>
                        </div>
                        <button className="ca-icon-btn ca-icon-btn--del" onClick={()=>removeSum(i)}>{TrashIcon}</button>
                      </div>
                    ))}
                  </div>

                  {/* Stats */}
                  <div className="ca-sub-hdr">
                    <div>
                      <p className="ca-sub-title">Stats — Program at a Glance</p>
                      <p className="ca-sub-desc">e.g. "1 Day", "20+ Experts" shown in pink card</p>
                    </div>
                    <button className="ca-add-btn" onClick={addStat}>{PlusIcon} Add Stat</button>
                  </div>

                  <div className="ca-list">
                    {conf.stats.map((s, i) => (
                      <div key={i} className="ca-list-row">
                        <div className="ca-list-num ca-list-num--purple">#{i+1}</div>
                        <div className="ca-grid-2 ca-list-inputs">
                          <input className="ca-input" value={s.value}
                            onChange={(e)=>updStat(i,"value",e.target.value)}
                            placeholder="e.g. 20+"/>
                          <input className="ca-input" value={s.label}
                            onChange={(e)=>updStat(i,"label",e.target.value)}
                            placeholder="e.g. EXPERTS"/>
                        </div>
                        {conf.stats.length > 1 && (
                          <button className="ca-icon-btn ca-icon-btn--del" onClick={()=>removeStat(i)}>{TrashIcon}</button>
                        )}
                      </div>
                    ))}
                  </div>
                </SectionCard>

                {/* Focus Areas */}
                <SectionCard
                  title="Right Side — Area of Focus Cards"
                  subtitle="Icon, title and description for each focus area card"
                  icon={FocusIcon} iconColor={C_PURPLE} iconBg="rgba(139,47,201,0.1)" delay={0.1}
                >
                  <div className="ca-grid-2">
                    <Field label="Section Title — Main">
                      <input className="ca-input" value={conf.titleRightMain}
                        onChange={(e)=>cSet("titleRightMain",e.target.value)}
                        placeholder="Area of"/>
                    </Field>
                    <Field label="Section Title — Pink Highlight">
                      <input className="ca-input" value={conf.titleRightHighlight}
                        onChange={(e)=>cSet("titleRightHighlight",e.target.value)}
                        placeholder="Focus"/>
                    </Field>
                  </div>

                  <div className="ca-focus-list">
                    {conf.focusAreas.map((fa, i) => (
                      <div key={i} className="ca-focus-row">
                        <div className="ca-focus-num">{i+1}</div>
                        <div className="ca-focus-body">
                          <div className="ca-grid-2">
                            <Field label="Card Title">
                              <input className="ca-input" value={fa.title}
                                onChange={(e)=>updFocus(i,"title",e.target.value)}
                                placeholder="e.g. Global Expertise"/>
                            </Field>
                            <Field label="Icon">
                              <div className="ca-icon-picker">
                                {ICON_OPTIONS.map((opt) => (
                                  <button key={opt.key} type="button"
                                    className={`ca-icon-opt ${fa.iconKey===opt.key?"ca-icon-opt--active":""}`}
                                    onClick={()=>updFocus(i,"iconKey",opt.key)}
                                    title={opt.key}>
                                    {opt.svg}
                                  </button>
                                ))}
                              </div>
                            </Field>
                          </div>
                          <Field label="Card Description">
                            <textarea className="ca-textarea" rows={2} value={fa.desc}
                              onChange={(e)=>updFocus(i,"desc",e.target.value)}
                              placeholder="Short description of this focus area…"/>
                          </Field>
                        </div>
                        {conf.focusAreas.length > 1 && (
                          <button className="ca-icon-btn ca-icon-btn--del" style={{marginTop:12}}
                            onClick={()=>removeFocus(i)}>{TrashIcon}</button>
                        )}
                      </div>
                    ))}
                  </div>

                  <button className="ca-add-btn ca-add-btn--full" onClick={addFocus}>
                    {PlusIcon} Add Focus Area
                  </button>
                </SectionCard>
              </div>
            )}

            {/* ══ FEEDBACK TAB ══ */}
            {tab === "feedback" && (
              <div className="ca-panel">
                <SectionCard
                  title="Feedback Section — 'Did You Know?'"
                  subtitle="Title, paragraphs with **bold** markers, and right-side stat boxes"
                  icon={FbIcon} iconColor={C_PINK} iconBg="rgba(240,77,160,0.1)" delay={0.05}
                >
                  <div className="ca-grid-3">
                    <Field label="Eyebrow Text">
                      <input className="ca-input" value={fb.eyebrowText}
                        onChange={(e)=>fSet("eyebrowText",e.target.value)}
                        placeholder="Participant Insights"/>
                    </Field>
                    <Field label="Title — Main">
                      <input className="ca-input" value={fb.titleMain}
                        onChange={(e)=>fSet("titleMain",e.target.value)}
                        placeholder="Did you"/>
                    </Field>
                    <Field label="Title — Pink Highlight">
                      <input className="ca-input" value={fb.titleHighlight}
                        onChange={(e)=>fSet("titleHighlight",e.target.value)}
                        placeholder="know?"/>
                    </Field>
                  </div>

                  <Field label="First Paragraph">
                    <textarea className="ca-textarea" rows={3} value={fb.para1}
                      onChange={(e)=>fSet("para1",e.target.value)}
                      placeholder="We are incredibly proud… **98% of attendees** reported…"/>
                    <span className="ca-field-hint">
                      💡 Wrap text in **double asterisks** to make it bold pink on frontend. E.g: <code>**98% of attendees**</code>
                    </span>
                  </Field>

                  <Field label="Second Paragraph">
                    <textarea className="ca-textarea" rows={3} value={fb.para2}
                      onChange={(e)=>fSet("para2",e.target.value)}
                      placeholder="Furthermore, **96% of participants** stated…"/>
                  </Field>

                  {/* Feedback Stats */}
                  <div className="ca-sub-hdr">
                    <div>
                      <p className="ca-sub-title">Stat Boxes (Right Side)</p>
                      <p className="ca-sub-desc">Large number + label shown on right side of feedback card</p>
                    </div>
                    <button className="ca-add-btn" onClick={addFbStat}>{PlusIcon} Add Stat</button>
                  </div>

                  <div className="ca-fb-stats-preview">
                    {fb.stats.map((s, i) => (
                      <div key={i} className="ca-fb-stat-preview-card">
                        <div className="ca-fb-stat-num">{s.value || "—"}<span className="ca-fb-pct">%</span></div>
                        <div className="ca-fb-stat-lbl">{s.label || "Label"}</div>
                      </div>
                    ))}
                  </div>

                  <div className="ca-list">
                    {fb.stats.map((s, i) => (
                      <div key={i} className="ca-list-row">
                        <div className="ca-list-num ca-list-num--purple">#{i+1}</div>
                        <div className="ca-grid-2 ca-list-inputs">
                          <Field label="Value (number)">
                            <input className="ca-input" value={s.value}
                              onChange={(e)=>updFbStat(i,"value",e.target.value)}
                              placeholder="e.g. 98"/>
                          </Field>
                          <Field label="Label">
                            <input className="ca-input" value={s.label}
                              onChange={(e)=>updFbStat(i,"label",e.target.value)}
                              placeholder="e.g. HIGHLY SATISFIED"/>
                          </Field>
                        </div>
                        {fb.stats.length > 1 && (
                          <button className="ca-icon-btn ca-icon-btn--del" style={{marginTop:20}}
                            onClick={()=>removeFbStat(i)}>{TrashIcon}</button>
                        )}
                      </div>
                    ))}
                  </div>
                </SectionCard>
              </div>
            )}

            {/* Bottom save bar */}
            <div className="ca-save-bar">
              <div className="ca-save-info">
                <svg width="13" height="13" fill="none" stroke="#9a86b8" strokeWidth="2"
                  strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                Saves both Conference Overview and Feedback section together.
              </div>
              <button className="ca-save-btn" onClick={save} disabled={saving}>
                {saving
                  ? <><span className="ca-btn-spin"/><span>Saving…</span></>
                  : <>{SaveIcon}<span>Save All Changes</span></>
                }
              </button>
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes ca-fadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
        @keyframes ca-spin{to{transform:rotate(360deg)}}
        @keyframes ca-pulse{0%,100%{opacity:1}50%{opacity:.4}}
        @keyframes ca-toast-in{from{opacity:0;transform:translateX(28px)}to{opacity:1;transform:translateX(0)}}
        *,*::before,*::after{box-sizing:border-box}

        .ca-page{background:#f4eefa;min-height:100vh;padding:28px 32px 80px;display:flex;flex-direction:column;gap:22px;font-family:inherit;}
        .ca-header{animation:ca-fadeUp .4s ease both;}
        .ca-eyebrow{display:inline-flex;align-items:center;gap:7px;font-size:11px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:${C_PINK};background:rgba(240,77,160,0.08);border:1px solid rgba(240,77,160,0.2);padding:5px 14px 5px 10px;border-radius:50px;margin-bottom:12px;}
        .ca-eyebrow-dot{width:6px;height:6px;border-radius:50%;background:${C_PINK};animation:ca-pulse 2s infinite;flex-shrink:0;}
        .ca-header-row{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;flex-wrap:wrap;}
        .ca-page-title{font-size:clamp(22px,2.5vw,30px);font-weight:800;color:${C_DARK};letter-spacing:-.025em;margin:0 0 6px;}
        .ca-grad{background:linear-gradient(90deg,${C_PINK},#c066f0);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;}
        .ca-page-sub{font-size:13.5px;color:#8a7a9a;margin:0;}

        .ca-loading{display:flex;align-items:center;gap:12px;padding:60px;justify-content:center;color:#8a7a9a;font-size:14px;}
        .ca-spinner{width:28px;height:28px;border-radius:50%;border:3px solid rgba(240,77,160,0.15);border-top-color:${C_PINK};animation:ca-spin .75s linear infinite;}

        /* Tabs */
        .ca-tabs{display:flex;gap:8px;flex-wrap:wrap;}
        .ca-tab{display:inline-flex;align-items:center;gap:7px;padding:10px 20px;border-radius:12px;border:1.5px solid #e8d8f8;background:#fff;color:#8a7a9a;font-size:13px;font-weight:700;cursor:pointer;transition:all .2s;font-family:inherit;}
        .ca-tab--active{background:${C_GRAD};color:#fff;border-color:transparent;box-shadow:0 6px 18px rgba(240,77,160,0.28);}
        .ca-tab:not(.ca-tab--active):hover{border-color:${C_PINK};color:${C_PINK};}

        /* Panel */
        .ca-panel{display:flex;flex-direction:column;gap:20px;}

        /* Section card */
        .ca-card{background:#fff;border-radius:20px;border:1px solid #ede5f5;box-shadow:0 4px 20px rgba(26,0,64,.06);overflow:hidden;animation:ca-fadeUp .4s ease both;}
        .ca-card-hdr{display:flex;align-items:center;gap:14px;padding:20px 26px 18px;border-bottom:1px solid #f3ecfa;background:#faf6fe;}
        .ca-card-ico{width:44px;height:44px;border-radius:12px;flex-shrink:0;display:flex;align-items:center;justify-content:center;}
        .ca-card-title{font-size:16px;font-weight:800;color:${C_DARK};margin:0 0 2px;}
        .ca-card-sub{font-size:12px;color:#9a86b8;margin:0;}
        .ca-card-body{padding:24px 26px 28px;display:flex;flex-direction:column;gap:18px;}

        /* Fields */
        .ca-field{display:flex;flex-direction:column;gap:6px;}
        .ca-label{font-size:12px;font-weight:700;color:#5a5070;letter-spacing:.03em;}
        .ca-field-hint{font-size:11px;color:#9a86b8;margin-top:4px;}
        .ca-field-hint code{background:rgba(240,77,160,0.08);padding:1px 5px;border-radius:4px;font-size:11px;color:${C_PINK};}
        .ca-input{height:44px;border-radius:11px;border:1.5px solid #e8d8f8;padding:0 14px;font-size:13.5px;color:${C_DARK};background:#fdfbff;outline:none;font-family:inherit;width:100%;transition:border-color .2s,box-shadow .2s;}
        .ca-input:focus{border-color:${C_PINK};box-shadow:0 0 0 3px rgba(240,77,160,0.12);background:#fff;}
        .ca-input::placeholder{color:#c4b4d8;}
        .ca-textarea{border-radius:11px;border:1.5px solid #e8d8f8;padding:12px 14px;font-size:13.5px;color:${C_DARK};background:#fdfbff;outline:none;font-family:inherit;width:100%;resize:vertical;line-height:1.6;transition:border-color .2s,box-shadow .2s;}
        .ca-textarea:focus{border-color:${C_PINK};box-shadow:0 0 0 3px rgba(240,77,160,0.12);background:#fff;}
        .ca-textarea::placeholder{color:#c4b4d8;}
        .ca-grid-2{display:grid;grid-template-columns:1fr 1fr;gap:14px;}
        .ca-grid-3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:14px;}
        .ca-empty-sm{font-size:12px;color:#b4a4c8;margin:0;padding:4px 0;}

        /* Sub headers */
        .ca-sub-hdr{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;}
        .ca-sub-title{font-size:13.5px;font-weight:700;color:${C_DARK};margin:0 0 3px;}
        .ca-sub-desc{font-size:12px;color:#9a86b8;margin:0;}

        /* List rows */
        .ca-list{display:flex;flex-direction:column;gap:10px;}
        .ca-list-row{display:flex;align-items:flex-start;gap:10px;background:#faf6fe;border:1px solid #ede5f5;border-radius:12px;padding:12px 14px;}
        .ca-list-num{width:26px;height:26px;border-radius:7px;background:${C_GRAD};color:#fff;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:800;flex-shrink:0;margin-top:9px;}
        .ca-list-num--purple{background:rgba(139,47,201,0.12);color:${C_PURPLE};}
        .ca-list-inputs{flex:1;display:flex;flex-direction:column;gap:8px;}

        /* Focus area rows */
        .ca-focus-list{display:flex;flex-direction:column;gap:14px;}
        .ca-focus-row{display:flex;align-items:flex-start;gap:12px;background:#faf6fe;border:1px solid #ede5f5;border-radius:14px;padding:16px;}
        .ca-focus-num{width:28px;height:28px;border-radius:8px;background:rgba(139,47,201,0.12);color:${C_PURPLE};display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:800;flex-shrink:0;margin-top:4px;}
        .ca-focus-body{flex:1;display:flex;flex-direction:column;gap:12px;}
        .ca-icon-picker{display:flex;gap:6px;flex-wrap:wrap;}
        .ca-icon-opt{width:36px;height:36px;border-radius:9px;border:1.5px solid #e8d8f8;background:#fff;display:flex;align-items:center;justify-content:center;cursor:pointer;color:#9a86b8;transition:all .2s;}
        .ca-icon-opt--active{border-color:${C_PINK};background:rgba(240,77,160,0.08);color:${C_PINK};}
        .ca-icon-opt:hover{border-color:${C_PINK};color:${C_PINK};}

        /* Feedback stats preview */
        .ca-fb-stats-preview{display:flex;gap:12px;flex-wrap:wrap;}
        .ca-fb-stat-preview-card{background:linear-gradient(135deg,rgba(240,77,160,0.07),rgba(139,47,201,0.07));border:1px solid rgba(240,77,160,0.15);border-radius:14px;padding:16px 22px;min-width:140px;}
        .ca-fb-stat-num{font-size:32px;font-weight:800;color:${C_DARK};line-height:1;margin-bottom:4px;}
        .ca-fb-pct{font-size:18px;color:${C_PINK};}
        .ca-fb-stat-lbl{font-size:11px;font-weight:700;color:#9a86b8;text-transform:uppercase;letter-spacing:.08em;}

        /* Add btn */
        .ca-add-btn{display:inline-flex;align-items:center;gap:7px;padding:8px 16px;border-radius:10px;border:1.5px solid rgba(240,77,160,0.3);background:rgba(240,77,160,0.06);color:${C_PINK};font-size:12.5px;font-weight:700;cursor:pointer;transition:background .2s;font-family:inherit;}
        .ca-add-btn:hover{background:rgba(240,77,160,0.12);}
        .ca-add-btn--full{width:100%;justify-content:center;}

        /* Icon btn */
        .ca-icon-btn{width:30px;height:30px;border-radius:8px;border:1.5px solid #e8d8f8;background:#fff;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all .2s;color:#8a7a9a;flex-shrink:0;}
        .ca-icon-btn--del:hover{border-color:#ef4444;color:#ef4444;background:#fef2f2;}

        /* Save bar */
        .ca-save-bar{display:flex;align-items:center;justify-content:space-between;background:#fff;border-radius:16px;border:1px solid #ede5f5;box-shadow:0 4px 20px rgba(26,0,64,.06);padding:18px 26px;gap:16px;flex-wrap:wrap;}
        .ca-save-info{display:flex;align-items:center;gap:8px;font-size:12px;color:#9a86b8;font-weight:500;}
        .ca-save-btn{display:inline-flex;align-items:center;gap:9px;padding:12px 26px;background:${C_GRAD};color:#fff;font-size:13.5px;font-weight:700;border:none;border-radius:12px;cursor:pointer;box-shadow:0 6px 20px rgba(240,77,160,0.3);transition:transform .2s,box-shadow .2s;font-family:inherit;white-space:nowrap;}
        .ca-save-btn:hover:not(:disabled){transform:translateY(-1px);box-shadow:0 10px 28px rgba(240,77,160,0.42);}
        .ca-save-btn:disabled{opacity:.65;cursor:not-allowed;}
        .ca-btn-spin{width:15px;height:15px;border-radius:50%;border:2px solid rgba(255,255,255,0.3);border-top-color:#fff;animation:ca-spin .7s linear infinite;}

        /* Toasts */
        .ca-toast-wrap{position:fixed;top:20px;right:20px;z-index:9999;display:flex;flex-direction:column;gap:8px;pointer-events:none;}
        .ca-toast{display:inline-flex;align-items:center;gap:9px;padding:11px 16px;border-radius:12px;font-size:13px;font-weight:600;box-shadow:0 8px 24px rgba(26,0,64,.15);animation:ca-toast-in .35s cubic-bezier(.16,1,.3,1) both;max-width:300px;}
        .ca-toast--success{background:#f0fdf4;color:#16a34a;border:1px solid #bbf7d0;}
        .ca-toast--error{background:#fef2f2;color:#dc2626;border:1px solid #fecaca;}

        @media(max-width:768px){
          .ca-page{padding:16px 16px 60px;gap:16px;}
          .ca-grid-2,.ca-grid-3{grid-template-columns:1fr;}
          .ca-header-row{flex-direction:column;}
          .ca-save-bar{flex-direction:column;align-items:stretch;}
          .ca-save-btn{justify-content:center;}
          .ca-card-body{padding:18px;}
        }
      `}</style>
    </App>
  );
}
